import {
  ActionValidation,
  BooleanValidation,
  ConfigOptionValidation,
  StringValidation,
} from '../../../../utils/component-validations';
import { or } from 'ember-truth-helpers';
import { testAwareWaitForLater } from '../../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import Body from './message/body.gjs';
import CTA from './message/cta.gjs';
import CloseButton from '../../action/button/close.gjs';
import FlexColumn from '../../layout/flex/column.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import Super from '../../core/base.gjs';
import styles from './message.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export const ALERT_BANNER_ERROR_TYPE = 'error';
export const ALERT_BANNER_INFO_TYPE = 'info';
export const ALERT_BANNER_SUCCESS_TYPE = 'success';
export const ALERT_BANNER_WARNING_TYPE = 'warning';
export const BODY_COLUMN_DEFAULT = true;
export const CTA_GAP_DEFAULT = 16;
export const MESSAGE_ALIGN_ITEMS_DEFAULT = 'flex-start';
export const MESSAGE_FLEX_DEFAULT = '1';
export const MESSAGE_GAP_DEFAULT = 12;

const COLUMN = 'column';
const CTA_ALIGNMENTS = [COLUMN, 'row'];
const SUPPORTED_TYPES = [
  ALERT_BANNER_ERROR_TYPE,
  ALERT_BANNER_INFO_TYPE,
  ALERT_BANNER_SUCCESS_TYPE,
  ALERT_BANNER_WARNING_TYPE,
];

export default class SpdsFeedbackAlertBannerMessage extends Super {
  arguments = [
    new ActionValidation({ optional: ['onClose'] }),
    new BooleanValidation({ optional: ['bodyColumn', 'closeable', 'includeIcon'] }),
    new ConfigOptionValidation({
      optional: [
        {
          name: 'ctaAlignment',
          options: CTA_ALIGNMENTS,
        },
      ],
      required: [
        {
          name: 'type',
          options: SUPPORTED_TYPES,
        },
      ],
    }),
    new StringValidation({ optional: ['icon', 'bodyTag'] }),
  ];
  baseClass = 'spds-feedback-alert-banner-message-container';
  classesToMapToArgs = ['global', 'type'];
  classesToMapToAttrs = ['open'];
  componentName = 'Spds::AlertBanner::Message';
  styles = styles;

  @tracked open = true;

  get bodyColumn() {
    return this.args.bodyColumn ?? BODY_COLUMN_DEFAULT;
  }

  get bodyTextComponent() {
    return this.bodyColumn ? FlexColumn : FlexRow;
  }

  get bodyTextTag() {
    return this.args.bodyTag || 'p';
  }

  get ctaAlignment() {
    return this.args.ctaAlignment === COLUMN;
  }

  get ctaAlignmentComponent() {
    return this.ctaAlignment ? FlexColumn : FlexRow;
  }

  get ctaGap() {
    return (this.args.ctaGap ?? CTA_GAP_DEFAULT) + 'px';
  }

  get icon() {
    let { icon, includeIcon, type } = this.args;

    if (icon) return icon;

    if (includeIcon) {
      switch (type) {
        case 'error':
          return 'triangle-exclamation-mark';
        case 'success':
          return 'circle-check';
        default:
          return 'circle-i';
      }
    }

    return null;
  }

  get messageGap() {
    return (this.args.messageGap ?? MESSAGE_GAP_DEFAULT) + 'px';
  }

  get messageAlignItems() {
    return this.args.messageAlignItems ?? MESSAGE_ALIGN_ITEMS_DEFAULT;
  }

  get messageFlex() {
    return this.args.messageFlex ?? MESSAGE_FLEX_DEFAULT;
  }

  get showCloseBtn() {
    let { closeable, global, onClose } = this.args;

    return (closeable && global) || onClose;
  }

  close = async () => {
    this.open = false;

    await testAwareWaitForLater(300);

    this.args.onClose?.();
  };

  <template>
    <FlexRow
      @fitContentHeight={{true}}
      @flex="1"
      class={{this.classes}}
      data-test-spds-feedback-alert-banner-message-container
      {{this.validateArguments}}
      ...attributes
    >
      <FlexRow
        @alignItems={{this.messageAlignItems}}
        @fitContentHeight={{true}}
        @flex={{this.messageFlex}}
        @gap={{this.messageGap}}
        class="spds-feedback-alert-banner-message"
        data-test-spds-feedback-alert-banner-message
      >
        {{#if this.icon}}
          {{svgJar
            this.icon
            class="spds-feedback-alert-banner-icon"
            data-test-spds-feedback-alert-banner-icon=""
          }}
        {{/if}}
        <this.ctaAlignmentComponent
          class="spds-feedback-alert-banner-body"
          data-test-spds-feedback-alert-banner-body
        >
          <FlexColumn
            class="spds-feedback-alert-banner-text-container"
            data-test-spds-feedback-alert-banner-text-container
          >
            {{#if (or (has-block "header") @header)}}
              <FlexRow
                @content={{@header}}
                @tag="h5"
                class="spds-feedback-alert-banner-header"
                data-test-spds-feedback-alert-banner-header
              >
                {{yield to="header"}}
              </FlexRow>
            {{/if}}
            <this.bodyTextComponent
              @tag={{this.bodyTextTag}}
              class="spds-feedback-alert-banner-body-text"
              data-test-spds-feedback-alert-banner-body-text
            >
              {{#if (has-block "body")}}
                {{yield to="body"}}
              {{else}}
                <Body @body={{@body}} />
              {{/if}}
            </this.bodyTextComponent>
          </FlexColumn>
          <FlexRow
            @gap={{this.ctaGap}}
            class="spds-feedback-alert-banner-cta-container"
            data-test-spds-feedback-alert-banner-cta-container
          >
            {{#if (has-block "cta-container")}}
              {{yield to="cta-container"}}
            {{else}}
              <CTA
                @onPrimaryBtnClick={{@onPrimaryBtnClick}}
                @onSecondaryBtnClick={{@onSecondaryBtnClick}}
                @primaryBtnText={{@primaryBtnText}}
                @secondaryBtnText={{@secondaryBtnText}}
              />
            {{/if}}
          </FlexRow>
        </this.ctaAlignmentComponent>
        {{#if this.showCloseBtn}}
          <CloseButton
            @close={{this.close}}
            class="spds-feedback-alert-banner-close"
            data-test-spds-feedback-alert-banner-close-button
          />
        {{/if}}
      </FlexRow>
    </FlexRow>
  </template>
}
